// LeadKanbanMobile — the mobile-only view of the lead pipeline. Desktop
// keeps the horizontal-scroll grid; this swaps the "swipe sideways
// through 6 columns" pattern for a one-column-per-page carousel.
//
// Why a carousel instead of horizontal scroll on mobile:
//   - Horizontal scroll inside the page makes the page itself feel
//     stuck — the kanban "steals" the swipe.
//   - One column per page lets the user swipe between statuses with a
//     decisive gesture and gives each column the full viewport width
//     for its cards (no cramped 220px columns).
//
// Tracks design source: `playground/lead kaban mobile.html` — the pill
// row, position dots, and per-column page layout.
//
// Composition:
//   - Pill row (tappable, horizontal-scroll for overflow). Tap = jump.
//   - Embla carousel — one LeadColumn per slide. Reuses the same
//     LeadColumn component the desktop grid uses, so the empty-state
//     copy and the "+" button behaviour stay in one place.
//
// We swallow drag-and-drop on mobile (`enableDrag={false}` on the cards)
// and rely on tapping a card → detail page → change-status sheet. The
// desktop pattern of dragging across columns doesn't translate to a
// single-column view anyway.
//
// We previously rendered a row of position dots between the pills and
// the carousel; that was redundant with the pill row (the active pill
// already tells you where you are), so it's been removed.

import { useCallback, useEffect, useState } from "react";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import {
  LEAD_STATUSES,
  LEAD_STATUS_DOT_COLORS,
  LEAD_STATUS_LABELS,
} from "@/features/lead/constants";
import { LeadColumn } from "@/features/lead/components/lead-column";
import type { Lead, LeadStatus } from "@/features/lead/types";

interface LeadKanbanMobileProps {
  leadsByStatus: Record<LeadStatus, Lead[]>;
  onOpenLead: (lead: Lead) => void;
  onAddLead: (status: LeadStatus) => void;
}

// Tone applied to the active pill — soft tinted background + saturated
// text, hue tracking the status itself. Sits in the same family as the
// column dot palette so the pill row and the dot row read as a unit.
const PILL_ACTIVE_TONE: Record<LeadStatus, string> = {
  NEW: "border-slate-300 bg-slate-100 text-slate-800",
  CONTACTED: "border-indigo-200 bg-indigo-50 text-indigo-700",
  VIEWING_SCHEDULED: "border-amber-200 bg-amber-50 text-amber-800",
  NEGOTIATING: "border-indigo-300 bg-indigo-100 text-indigo-800",
  WON: "border-emerald-200 bg-emerald-50 text-emerald-700",
  LOST: "border-rose-200 bg-rose-50 text-rose-700",
};

export function LeadKanbanMobile({
  leadsByStatus,
  onOpenLead,
  onAddLead,
}: LeadKanbanMobileProps) {
  // Embla doesn't expose its selected index via React props; we wire it
  // through the `setApi` callback the shadcn wrapper exposes, then
  // subscribe to the `select` event to keep our local index in sync
  // with both swipe gestures and programmatic scrollTo() calls (pill
  // taps).
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (!api) return;
    const sync = () => setSelectedIndex(api.selectedScrollSnap());
    sync();
    api.on("select", sync);
    api.on("reInit", sync);
    return () => {
      api.off("select", sync);
      api.off("reInit", sync);
    };
  }, [api]);

  const scrollTo = useCallback(
    (index: number) => {
      api?.scrollTo(index);
    },
    [api],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Pill row — tappable jump targets. The active pill is tinted in
          its status hue; inactive pills sit as a quiet ghost outline so
          the active one reads first. The row scrolls horizontally when
          there's not enough room (always the case at typical phone
          widths with 6 statuses). */}
      <div
        role="tablist"
        aria-label="Trạng thái pipeline"
        // Negative-margin to bleed to the edge so the row scrolls
        // edge-to-edge rather than stopping at the page padding.
        className="-mx-6 flex gap-1.5 overflow-x-auto px-6 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {LEAD_STATUSES.map((status, idx) => {
          const active = idx === selectedIndex;
          const count = leadsByStatus[status].length;
          return (
            <button
              key={status}
              role="tab"
              type="button"
              aria-selected={active}
              onClick={() => scrollTo(idx)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors",
                active
                  ? PILL_ACTIVE_TONE[status]
                  : "border-border bg-card text-muted-foreground hover:bg-muted",
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "size-1.5 shrink-0 rounded-full",
                  LEAD_STATUS_DOT_COLORS[status],
                )}
              />
              <span>{LEAD_STATUS_LABELS[status]}</span>
              <span
                className={cn(
                  "font-mono text-[11px] tabular-nums",
                  active ? "opacity-80" : "opacity-60",
                )}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Carousel — one column per page. The negative margin lets the
          slides bleed to the viewport edges; the inner LeadColumn keeps
          its own padding so cards still breathe inside. */}
      <Carousel
        opts={{ align: "start", containScroll: "trimSnaps" }}
        setApi={setApi}
        className="-mx-6"
      >
        <CarouselContent
          // Override the wrapper's default `-ml-4` and the slide's
          // `pl-4` so each slide spans the full viewport width without
          // an offset gutter — the design has columns flush to the
          // page edges with column-internal padding only.
          className="ml-0"
        >
          {LEAD_STATUSES.map((status) => (
            <CarouselItem key={status} className="basis-full pl-0">
              <div className="px-6">
                <LeadColumn
                  status={status}
                  leads={leadsByStatus[status]}
                  enableDrag={false}
                  onOpenLead={onOpenLead}
                  onAddLead={onAddLead}
                  cardStatusUI="pill"
                />
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {/* Swipe hint — quiet enough not to nag returning users but
          visible enough to teach the gesture the first time. Sits
          under the carousel so it doesn't push the column down. */}
      <p
        aria-hidden="true"
        className="text-center font-mono text-[11px] text-ink-faint"
      >
        ← Vuốt để chuyển trạng thái →
      </p>
    </div>
  );
}
